import { useCallback, useState } from "react";
import {
  Paper,
  Stack,
  Text,
  Alert,
  Divider,
  Select,
  TextInput,
  Group,
  Box,
  Tooltip,
  ActionIcon,
  Badge,
  Switch,
  Checkbox,
  Loader,
  Collapse,
  UnstyledButton,
} from "@mantine/core";
import {
  IconQuestionMark,
  IconChevronDown,
  IconChevronUp,
  IconSettings,
  IconAlertTriangle,
  IconInfoCircle,
  IconLock,
} from "@tabler/icons-react";
import { getContrastColor } from "../../utils/helpers";
import type { PriorityTrigger } from "../../types";

const BILLING_SCENARIO_DESCRIPTIONS: Record<string, string> = {
  global: "GCP bills insurance for TC + PC — the full amount.",
  "tcpc split":
    "GCP bills insurance for TC only. The practice also submits a separate claim to insurance for PC.",
  "lab-to-lab":
    "GCP bills insurance for the full amount, then remits payment back to the referring lab.",
  "tc only": "GCP bills insurance for TC only. No PC claim is filed by anyone.",
  consultation: "GCP bills insurance for PC only. No TC claim is filed.",
  "direct-to-practice":
    "GCP invoices the practice directly. No insurance claim is filed.",
};

// Certainty helper functions
function getCertaintyLabel(certainty: number): string {
  if (certainty >= 95) return "High Certainty";
  if (certainty >= 85) return "Medium Certainty";
  if (certainty >= 75) return "Low Certainty";
  return "Review Required";
}

function getCertaintyBadgeColor(certainty: number): string {
  if (certainty >= 95) return "green";
  if (certainty >= 85) return "yellow";
  if (certainty >= 75) return "orange";
  return "red";
}

interface PrefixOption {
  value: string;
  label: string;
  color?: string | null;
}

interface CaseFieldsCardProps {
  // Case Signals — requisition-detected flags (AI pre-populated, staff-overridable)
  isCytology?: boolean;
  isResearch?: boolean;
  isConsultation?: boolean;
  isTcOnly?: boolean;
  isGrossingOnly?: boolean;
  onCytologyChange?: (value: boolean) => void;
  onResearchChange?: (value: boolean) => void;
  onConsultationChange?: (value: boolean) => void;
  onTcOnlyChange?: (value: boolean) => void;
  onGrossingOnlyChange?: (value: boolean) => void;
  // Assigned Prefix — auto-resolved + optional manual override
  prefixCode?: string | null;
  prefixColor?: string | null;
  isResolvingPrefix?: boolean;
  prefixOverride?: string | null;
  onPrefixOverrideChange?: (code: string | null) => void;
  availablePrefixes?: PrefixOption[];
  isPrefixReadOnly?: boolean;
  // Pathologist
  pathologists: Array<{ value: string; label: string }>;
  selectedPathologistId: string | null;
  assignmentReason?: string;
  onPathologistChange: (id: string | null) => void;
  // Billing Scenario
  selectedBillingScenarioId: string | null;
  onBillingScenarioChange: (id: string | null) => void;
  // Rush - physical RUSH stamp detection
  isRush?: boolean;
  rushReason?: string | null;
  onRushChange?: (isRush: boolean) => void;
  onRushReasonChange?: (reason: string) => void;
  rushCertainty?: number;
  onRushCertaintyChange?: (certainty: number) => void;
  // High Priority - clinical keyword detection
  isHighPriority?: boolean;
  highPriorityReason?: string | null;
  onHighPriorityChange?: (isHighPriority: boolean) => void;
  onHighPriorityReasonChange?: (reason: string) => void;
  highPriorityCertainty?: number;
  onHighPriorityCertaintyChange?: (certainty: number) => void;
  highPriorityTriggers?: PriorityTrigger[];
  // Sales Rep - case came through a sales representative
  isThroughSalesRep?: boolean;
  salesRepId?: string | null;
  salesReps?: Array<{ value: string; label: string }>;
  onThroughSalesRepChange?: (value: boolean) => void;
  onSalesRepChange?: (id: string | null) => void;
  // When set, the billing scenario field becomes read-only (single option) or constrained select
  prefixPossibleBillingScenarios?: { id: string; name: string }[];
  // Limited-edit mode: locks accessioningType and billingScenario
  isLimitedEdit?: boolean;
}

export default function CaseFieldsCard({
  isCytology = false,
  isResearch = false,
  isConsultation = false,
  isTcOnly = false,
  isGrossingOnly = false,
  onCytologyChange,
  onResearchChange,
  onConsultationChange,
  onTcOnlyChange,
  onGrossingOnlyChange,
  prefixCode,
  prefixColor,
  isResolvingPrefix = false,
  prefixOverride,
  onPrefixOverrideChange,
  availablePrefixes = [],
  isPrefixReadOnly = false,
  pathologists,
  selectedPathologistId,
  assignmentReason,
  onPathologistChange,
  selectedBillingScenarioId,
  onBillingScenarioChange,
  isRush = false,
  rushReason,
  onRushChange,
  onRushReasonChange,
  rushCertainty,
  onRushCertaintyChange,
  isHighPriority = false,
  highPriorityReason,
  onHighPriorityChange,
  onHighPriorityReasonChange,
  highPriorityCertainty,
  onHighPriorityCertaintyChange,
  highPriorityTriggers = [],
  isThroughSalesRep = false,
  salesRepId,
  salesReps = [],
  onThroughSalesRepChange,
  onSalesRepChange,
  prefixPossibleBillingScenarios,
  isLimitedEdit = false,
}: CaseFieldsCardProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const handleExpandedToggle = useCallback(() => {
    setIsExpanded((expanded) => !expanded);
  }, []);
  const handleCytologyChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onCytologyChange?.(event.currentTarget.checked);
    },
    [onCytologyChange]
  );
  const handleResearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onResearchChange?.(event.currentTarget.checked);
    },
    [onResearchChange]
  );
  const handleConsultationChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onConsultationChange?.(event.currentTarget.checked);
    },
    [onConsultationChange]
  );
  const handleTcOnlyChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onTcOnlyChange?.(event.currentTarget.checked);
    },
    [onTcOnlyChange]
  );
  const handleGrossingOnlyChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onGrossingOnlyChange?.(event.currentTarget.checked);
    },
    [onGrossingOnlyChange]
  );

  return (
    <Paper p="sm" withBorder>
      <UnstyledButton
        onClick={handleExpandedToggle}
        style={{ width: "100%" }}
        aria-expanded={isExpanded}
        aria-label={
          isExpanded ? "Collapse case settings" : "Expand case settings"
        }
      >
        <Group justify="space-between">
          <Group gap="xs">
            <IconSettings size={16} color="var(--mantine-color-gray-6)" />
            <Text size="sm" fw={500}>
              Case Settings
            </Text>
          </Group>
          {isExpanded ? (
            <IconChevronUp size={16} aria-hidden="true" />
          ) : (
            <IconChevronDown size={16} aria-hidden="true" />
          )}
        </Group>
      </UnstyledButton>

      <Collapse in={isExpanded}>
        <Stack gap="md" mt="md">
          {/* Case Signals — set by AI extraction, overridable by staff */}
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700} mb={6}>
              Case Signals
            </Text>
            <Group gap="md">
              <Checkbox
                label="Cytology"
                checked={isCytology}
                onChange={handleCytologyChange}
                disabled={isLimitedEdit}
                size="sm"
              />
              <Checkbox
                label="Research"
                checked={isResearch}
                onChange={handleResearchChange}
                disabled={isLimitedEdit}
                size="sm"
              />
              <Checkbox
                label="Consultation"
                checked={isConsultation}
                onChange={handleConsultationChange}
                disabled={isLimitedEdit}
                size="sm"
              />
              <Checkbox
                label="TC Only"
                checked={isTcOnly}
                onChange={handleTcOnlyChange}
                disabled={isLimitedEdit}
                size="sm"
              />
              <Checkbox
                label="Grossing only"
                checked={isGrossingOnly}
                onChange={handleGrossingOnlyChange}
                disabled={isLimitedEdit}
                size="sm"
              />
            </Group>
          </div>

          {/* Assigned Prefix */}
          <div>
            <Group gap={4} mb={6}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Assigned Prefix
              </Text>
              {isResolvingPrefix && <Loader size="xs" color="gray" />}
            </Group>
            <Select
              placeholder={
                isResolvingPrefix ? "Resolving…" : "No prefix resolved"
              }
              value={prefixOverride ?? prefixCode ?? null}
              onChange={onPrefixOverrideChange ?? undefined}
              data={availablePrefixes}
              size="sm"
              disabled={isLimitedEdit || isPrefixReadOnly}
              clearable
              searchable
              leftSection={(() => {
                const effectiveCode = prefixOverride ?? prefixCode;
                const effectiveColor = prefixOverride
                  ? availablePrefixes.find((p) => p.value === prefixOverride)
                      ?.color
                  : prefixColor;
                if (!effectiveCode) return null;
                return effectiveColor ? (
                  <Box
                    style={{
                      backgroundColor: effectiveColor,
                      color: getContrastColor(effectiveColor),
                      borderRadius: 3,
                      padding: "1px 5px",
                      fontWeight: 700,
                      fontSize: 10,
                      fontFamily: "monospace",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {effectiveCode}
                  </Box>
                ) : (
                  <Badge variant="filled" color="blue" size="xs">
                    {effectiveCode}
                  </Badge>
                );
              })()}
              leftSectionWidth={prefixOverride ?? prefixCode ? 52 : undefined}
            />
            {isPrefixReadOnly && (
              <Text size="xs" c="dimmed" mt={4}>
                Use the case number correction action to change an assigned case
                identity.
              </Text>
            )}
            {prefixOverride && prefixCode && prefixOverride !== prefixCode && (
              <Text size="xs" c="dimmed" mt={4}>
                Auto-resolved: {prefixCode}
              </Text>
            )}
          </div>

          {/* Billing Scenario */}
          <div>
            {/* Scenario 2: Single possible billing scenario — read-only display */}
            {prefixPossibleBillingScenarios &&
            prefixPossibleBillingScenarios.length === 1 ? (
              <Box>
                <Text size="xs" fw={600} mb={4}>
                  Billing Scenario
                </Text>
                <Box
                  style={{
                    padding: "6px 10px",
                    background: "var(--mantine-color-gray-0)",
                    border: "1px solid var(--mantine-color-gray-3)",
                    borderRadius: "var(--mantine-radius-sm)",
                  }}
                >
                  <Group gap="xs" align="center">
                    <IconLock size={12} color="var(--mantine-color-gray-5)" />
                    <Text size="sm" fw={500}>
                      {prefixPossibleBillingScenarios[0].name}
                    </Text>
                    <Badge size="xs" variant="light" color="blue">
                      Set by prefix
                    </Badge>
                  </Group>
                </Box>
                {BILLING_SCENARIO_DESCRIPTIONS[
                  prefixPossibleBillingScenarios[0].name.toLowerCase()
                ] && (
                  <Alert
                    icon={<IconInfoCircle size={13} />}
                    color="teal"
                    variant="light"
                    mt={6}
                    py={6}
                    px={8}
                    styles={{ message: { fontSize: "11px" } }}
                  >
                    {
                      BILLING_SCENARIO_DESCRIPTIONS[
                        prefixPossibleBillingScenarios[0].name.toLowerCase()
                      ]
                    }
                  </Alert>
                )}
              </Box>
            ) : prefixPossibleBillingScenarios &&
              prefixPossibleBillingScenarios.length > 1 ? (
              /* Scenario 4/5: Multiple possible billing scenarios from prefix — constrained Select */
              <Box>
                <Tooltip
                  label="Options constrained by practice prefix"
                  position="top"
                  withArrow
                >
                  <Select
                    label="Billing Scenario"
                    placeholder="Select billing scenario"
                    data={prefixPossibleBillingScenarios.map((s) => ({
                      value: s.id,
                      label: s.name,
                    }))}
                    value={selectedBillingScenarioId}
                    onChange={onBillingScenarioChange}
                    searchable
                    size="sm"
                    disabled={isLimitedEdit}
                    maxDropdownHeight={360}
                    renderOption={({ option }) => {
                      const desc =
                        BILLING_SCENARIO_DESCRIPTIONS[
                          option.label.toLowerCase()
                        ];
                      return (
                        <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                          <Text size="sm" fw={500}>
                            {option.label}
                          </Text>
                          {desc && (
                            <Text
                              size="xs"
                              c="dimmed"
                              style={{ whiteSpace: "normal", lineHeight: 1.35 }}
                            >
                              {desc}
                            </Text>
                          )}
                        </Stack>
                      );
                    }}
                  />
                </Tooltip>
                {(() => {
                  const selected = prefixPossibleBillingScenarios.find(
                    (s) => s.id === selectedBillingScenarioId
                  );
                  const desc =
                    selected &&
                    BILLING_SCENARIO_DESCRIPTIONS[selected.name.toLowerCase()];
                  return desc ? (
                    <Alert
                      icon={<IconInfoCircle size={13} />}
                      color="teal"
                      variant="light"
                      mt={6}
                      py={6}
                      px={8}
                      styles={{ message: { fontSize: "11px" } }}
                    >
                      {desc}
                    </Alert>
                  ) : null;
                })()}
              </Box>
            ) : (
              /* Default: prefix not yet resolved — disabled placeholder */
              <Box>
                <Text size="xs" fw={600} mb={4}>
                  Billing Scenario
                </Text>
                <Select
                  placeholder="Determined by practice selection"
                  data={[]}
                  value={null}
                  onChange={() => undefined}
                  size="sm"
                  disabled
                />
              </Box>
            )}
          </div>

          {/* Assigned Pathologist */}
          <Divider />
          <div>
            <Select
              label="Assigned Pathologist"
              placeholder="Will be auto-assigned"
              data={pathologists}
              value={selectedPathologistId}
              onChange={onPathologistChange}
              searchable
              size="sm"
            />
            {assignmentReason && (
              <Text size="xs" c="dimmed" mt={4}>
                {assignmentReason}
              </Text>
            )}
          </div>

          <Divider />

          {/* Sales Rep Section */}
          <Box>
            <Switch
              label="Through Sales Rep"
              description="Enable if this case came through a sales representative"
              checked={isThroughSalesRep}
              onChange={(e) => {
                onThroughSalesRepChange?.(e.currentTarget.checked);
                if (!e.currentTarget.checked) {
                  onSalesRepChange?.(null);
                }
              }}
              size="sm"
              styles={{
                body: { cursor: "pointer" },
                track: { cursor: "pointer" },
                label: { cursor: "pointer" },
                description: { cursor: "pointer" },
              }}
            />
            {isThroughSalesRep && (
              <Select
                label="Sales Representative"
                placeholder="Select sales rep"
                data={salesReps}
                value={salesRepId}
                onChange={onSalesRepChange}
                searchable
                clearable
                size="sm"
                mt="sm"
              />
            )}
          </Box>

          <Divider />

          {/* Rush Section */}
          <Box
            p="sm"
            style={{
              backgroundColor: isRush
                ? "var(--mantine-color-red-0)"
                : "transparent",
              borderRadius: "var(--mantine-radius-sm)",
              transition: "background-color 0.2s ease",
            }}
          >
            <Stack gap="sm">
              <div>
                <Group gap={4} mb={4}>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Rush Status
                  </Text>
                  <Tooltip
                    label="Rush cases have a physical RUSH stamp and require expedited processing throughout the workflow."
                    position="top"
                  >
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color="gray"
                      aria-label="Rush status help"
                    >
                      <IconQuestionMark size={12} />
                    </ActionIcon>
                  </Tooltip>
                  {rushCertainty !== undefined && (
                    <Tooltip
                      label={getCertaintyLabel(rushCertainty)}
                      position="top"
                    >
                      <Badge
                        size="xs"
                        color={getCertaintyBadgeColor(rushCertainty)}
                        variant="filled"
                      >
                        {rushCertainty.toFixed(1)}%
                      </Badge>
                    </Tooltip>
                  )}
                </Group>
                <Switch
                  label="RUSH"
                  description="Expedited processing throughout workflow"
                  checked={isRush}
                  onChange={(event) => {
                    onRushChange?.(event.currentTarget.checked);
                    onRushCertaintyChange?.(100);
                  }}
                  color="red"
                  size="sm"
                  styles={{
                    body: { cursor: "pointer" },
                    track: { cursor: "pointer" },
                    label: { cursor: "pointer" },
                    description: { cursor: "pointer" },
                  }}
                />
              </div>

              {isRush && (
                <TextInput
                  label="Rush Reason"
                  placeholder="e.g., RUSH stamp detected on requisition"
                  value={rushReason || ""}
                  onChange={(event) =>
                    onRushReasonChange?.(event.currentTarget.value)
                  }
                  size="sm"
                />
              )}
            </Stack>
          </Box>

          <Divider />

          {/* High Priority Section */}
          <Box
            p="sm"
            style={{
              backgroundColor: isHighPriority
                ? "var(--mantine-color-orange-0)"
                : "transparent",
              borderRadius: "var(--mantine-radius-sm)",
              transition: "background-color 0.2s ease",
            }}
          >
            <Stack gap="sm">
              <div>
                <Group gap={4} mb={4}>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    High Priority
                  </Text>
                  <Tooltip
                    label="High priority cases have clinical keywords (mass, tumor, cancer) detected and are flagged for attention."
                    position="top"
                  >
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color="gray"
                      aria-label="High priority help"
                    >
                      <IconQuestionMark size={12} />
                    </ActionIcon>
                  </Tooltip>
                  {highPriorityCertainty !== undefined && (
                    <Tooltip
                      label={getCertaintyLabel(highPriorityCertainty)}
                      position="top"
                    >
                      <Badge
                        size="xs"
                        color={getCertaintyBadgeColor(highPriorityCertainty)}
                        variant="filled"
                      >
                        {highPriorityCertainty.toFixed(1)}%
                      </Badge>
                    </Tooltip>
                  )}
                </Group>
                <Switch
                  label="High Priority"
                  description="Flagged for attention (mass, tumor, cancer keywords)"
                  checked={isHighPriority}
                  onChange={(event) => {
                    onHighPriorityChange?.(event.currentTarget.checked);
                    onHighPriorityCertaintyChange?.(100);
                  }}
                  color="orange"
                  size="sm"
                  styles={{
                    body: { cursor: "pointer" },
                    track: { cursor: "pointer" },
                    label: { cursor: "pointer" },
                    description: { cursor: "pointer" },
                  }}
                />
              </div>

              {isHighPriority && (
                <>
                  <TextInput
                    label="Priority Reason"
                    placeholder="e.g., Specimen D — MASS"
                    value={highPriorityReason || ""}
                    onChange={(event) =>
                      onHighPriorityReasonChange?.(event.currentTarget.value)
                    }
                    size="sm"
                  />
                  {highPriorityTriggers.length > 0 && (
                    <Group gap={6} mt={4}>
                      <IconAlertTriangle
                        size={14}
                        color="var(--mantine-color-orange-6)"
                      />
                      {highPriorityTriggers.map((trigger, idx) => (
                        <Tooltip
                          key={idx}
                          label={`Found "${trigger.keyword}" in ${trigger.field}`}
                        >
                          <Badge color="orange" variant="light" size="sm">
                            Specimen {trigger.specimenId} — {trigger.keyword}
                          </Badge>
                        </Tooltip>
                      ))}
                    </Group>
                  )}
                </>
              )}
            </Stack>
          </Box>
        </Stack>
      </Collapse>
    </Paper>
  );
}
